import { Skeleton } from "@material-ui/lab";
import qs from "qs";
import * as React from "react";

import { useMarketResearchContext, useMarketResearchSummaryStyles } from "..";
import {
    Breakpoint,
    currencyCompactFormatter,
    Link,
    MarketResearchError,
    MarketResearchSearchCriteria,
    MarketResearchType,
    Text,
    useDeviceSize,
} from "../../..";
import { ResearchIcon } from "../../../assets/icons";

interface MarketResearchSummaryHeaderProps {
    marketResearchType: MarketResearchType;
}

export const MarketResearchSummaryHeader = (
    props: MarketResearchSummaryHeaderProps
) => {
    const { marketResearchType } = props;
    const {
        summary: { isBusy: summaryIsBusy, data: summaryData, error },
    } = useMarketResearchContext();
    const classes = useMarketResearchSummaryStyles({});
    const device = useDeviceSize();

    const summaryIsBusyOrLoadingWithoutErrors =
        summaryIsBusy || (!summaryData?.jobTitle?.jobName && !error);

    const getResearchToolLink = () => {
        const marketResearchCriteria: MarketResearchSearchCriteria = {
            jobName: summaryData?.jobTitle.jobName,
            locationName: summaryData?.locationName,
            locationId: summaryData?.locationId,
        };

        const queryString = qs.stringify(marketResearchCriteria, {
            skipNulls: true,
            addQueryPrefix: true,
            sort: (a, b) => a.localeCompare(b),
        });

        return `/market-research${queryString}`;
    };

    if (summaryIsBusyOrLoadingWithoutErrors) {
        return (
            <Skeleton variant="rect" height={137} style={{ marginBottom: 8 }} />
        );
    }

    if (!!error) {
        return (
            <div
                className={
                    device === "mobile"
                        ? classes.mobileHeader
                        : classes.marketHeader
                }
            >
                <div className={classes.displayFlexError}>
                    <MarketResearchError
                        className={classes.summaryError}
                        padding={false}
                        title="summary"
                        error={error}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            className={
                device === "mobile"
                    ? classes.mobileHeader
                    : classes.marketHeader
            }
        >
            <div className={classes.displayFlex}>
                <Breakpoint desktop>
                    <div className={classes.icon}>
                        <ResearchIcon fontSize="large" color="primary" />
                    </div>
                </Breakpoint>

                {/* In private job mode, hyperlink the name and location only if they have anytime access */}
                <div className={classes.displayContent}>
                    <Text variant="h2">
                        {"Data for "}
                        {marketResearchType.isPrivateJob() ? (
                            <Link
                                id="summaryJobTitle"
                                to={getResearchToolLink()}
                            >
                                {summaryData?.jobTitle.jobName}
                            </Link>
                        ) : (
                            <span className={classes.unlinkedSummaryTitle}>
                                {summaryData?.jobTitle.jobName}
                            </span>
                        )}
                        {" in "}
                        {marketResearchType.isPrivateJob() ? (
                            <Link
                                id="summaryLocationName"
                                to={getResearchToolLink()}
                            >
                                {summaryData?.locationName}
                            </Link>
                        ) : (
                            <span className={classes.unlinkedSummaryTitle}>
                                {summaryData?.locationName}
                            </span>
                        )}
                        {!!summaryData?.compensation?.annualSalaryInUSDollars
                            ? ` targeting a ${currencyCompactFormatter.format(
                                  summaryData.compensation
                                      .annualSalaryInUSDollars
                              )} annual salary`
                            : null}
                    </Text>
                </div>
            </div>
        </div>
    );
};
